import React, { FC } from 'react';

import { Form, FormInstance, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { LimitOwnerType } from '@app/models/products';
import { GroupsSelect, UsersSelect } from '@components/AntFields';
import { Paper } from '@components/Paper';

import { ProductsTable } from './components/ProductsTable';
import { IFormValues, ISelectedProductLimit } from './interfaces';

import { ValidationSchema } from './validation';

interface IProps {
  form: FormInstance<IFormValues>;
  onFinish: (formValues: IFormValues) => void;
  catalogUid?: string;
  selectedProductUids: ISelectedProductLimit[];
  onSelectProductUids: (uids: ISelectedProductLimit[]) => void;
  initialValues?: Partial<IFormValues>;
}

export const FormLimit: FC<IProps> = (props) => {
  const catalogsT = useTranslation(NSKeys.catalogs);
  const validationT = useTranslation(NSKeys.validation);
  const limitOwnerType = Form.useWatch('limit_owner_type', props.form);
  const prevLimitOwnerType = React.useRef(props.initialValues?.limit_owner_type ?? LimitOwnerType.User);

  React.useEffect(() => {
    if (prevLimitOwnerType.current !== limitOwnerType) {
      props.form.setFieldValue('limit_owner_uid', undefined);
    }
    prevLimitOwnerType.current = limitOwnerType;
  }, [props.form, limitOwnerType]);

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  return (
    <Paper style={{ margin: '24px', padding: '24px' }}>
      <Form<IFormValues>
        form={props.form}
        name="createCatalogLimitForm"
        id="createCatalogLimitForm"
        layout="vertical"
        initialValues={{ limit_owner_type: LimitOwnerType.User, ...props.initialValues }}
        onFinish={props.onFinish}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}>
        <Form.Item name="limit_owner_type" label={catalogsT.t('limits.ownerTypeLabel')} rules={[yupSync]}>
          <Select
            options={[
              { value: LimitOwnerType.User, label: catalogsT.t('limits.ownerTypeUser') },
              { value: LimitOwnerType.UserGroup, label: catalogsT.t('limits.ownerTypeGroup') },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="limit_owner_uid"
          label={
            limitOwnerType === LimitOwnerType.UserGroup
              ? catalogsT.t('limits.ownerGroupLabel')
              : catalogsT.t('limits.ownerUserLabel')
          }
          rules={[yupSync]}>
          {limitOwnerType === LimitOwnerType.UserGroup ? (
            <GroupsSelect labelInValue placeholder={catalogsT.t('limits.ownerGroupPlaceholder')} />
          ) : (
            <UsersSelect labelInValue placeholder={catalogsT.t('limits.ownerUserPlaceholder')} />
          )}
        </Form.Item>

        <Typography.Title level={5} style={{ margin: 0 }}>
          {catalogsT.t('limits.productsTableHint')}
        </Typography.Title>

        {props.catalogUid && (
          <ProductsTable
            catalog_uid={props.catalogUid}
            onSelectionChange={props.onSelectProductUids}
            selectedProductUids={props.selectedProductUids}
          />
        )}
      </Form>
    </Paper>
  );
};
